<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed } from 'vue'
import {
  getColorStyleData,
  getGradientStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '../../../composables/useStyleData'
import { usePipe } from '../../../composables/usePipe'
import { toLogicalTextAlign } from '@render/lib/textAlign'
import TipTap from '@render/lib/TipTap'
import type { TextFields as Model, TextStylePreset } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
})

const pipe = usePipe()

const value = computed({
  get: () => {
    return pipe.getTranslation(fields.value.body?.key)
  },
  set: (newVal: object) => {
    // Used in editor only. Every FieldValues<> key is optional (a stored pipe may
    // omit any field), so guard rather than assuming `body` is set.
    const key = fields.value.body?.key
    if (key) pipe.setTranslation(key, newVal)
  },
})

const activePreset = computed<Exclude<TextStylePreset, 'custom'> | null>(() => {
  const s = fields.value.textStyle
  return s && s !== 'custom' ? s : null
})

const presetVar = (prop: 'family' | 'weight' | 'size' | 'line-height' | 'color'): string | null =>
  activePreset.value ? `var(--${activePreset.value}-${prop})` : null

// Heading 1–4 presets render as real <h1>–<h4>; everything else stays a <div>.
// The swap is invisible: `.text-single` declares every box/typography property
// the UA and brand baseCss h1–h4 rules touch, and an unset v-bind resolves to
// `initial` (→ inherit/initial), exactly what the div gets. Keep those
// declarations — they are what neutralises heading defaults.
const HEADING_TAG_BY_PRESET: Partial<Record<TextStylePreset, 'h1' | 'h2' | 'h3' | 'h4'>> = {
  'text-heading-1': 'h1',
  'text-heading-2': 'h2',
  'text-heading-3': 'h3',
  'text-heading-4': 'h4',
}
const rootTag = computed(
  () => HEADING_TAG_BY_PRESET[fields.value.textStyle as TextStylePreset] ?? 'div',
)

const textFontFamily = computed(() => fields.value.fontFamily?.trim() || presetVar('family'))
const textFontWeight = computed(() => fields.value.fontWeight || presetVar('weight'))
const textGradient = computed(() => getGradientStyleData(fields.value.textGradient))
const hasGradient = computed(() => !!textGradient.value)
// A gradient wins over a solid color, same precedence StyledContainer uses for
// bgColor/bgGradient — `color` becomes transparent so the gradient (painted via
// background-clip below) is what's visible.
const textColor = computed(() =>
  hasGradient.value ? 'transparent' : (getColorStyleData(fields.value.color) ?? presetVar('color')),
)
const textFontSize = computed(() => getSizeStyleData(fields.value.fontSize) ?? presetVar('size'))
const textLineHeight = computed(
  () => getSizeStyleData(fields.value.lineHeight) ?? presetVar('line-height'),
)
// Logical alignment: an authored left/right mirrors under RTL (center/justify
// stay put). See toLogicalTextAlign.
const textAlign = computed(() => toLogicalTextAlign(fields.value.textAlign))
const textPadding = computed(() => getSpacingStyleData(fields.value.padding))
const textMargin = computed(() => getSpacingStyleData(fields.value.margin))

// Optional max-width cap with single-line ellipsis truncation. Only resolves to
// a value when the switcher is on AND a width is set, so the truncation CSS
// (gated on `isTruncated`) stays inert otherwise.
const textMaxWidth = computed(() =>
  fields.value.widthLimit?.enabled
    ? (getSizeStyleData(fields.value.widthLimit.maxWidth) ?? undefined)
    : undefined,
)
const isTruncated = computed(() => !!textMaxWidth.value)
</script>

<template>
  <component
    :is="rootTag"
    class="text-single"
    :class="{ 'is-truncated': isTruncated, 'has-gradient': hasGradient }"
  >
    <span v-if="!fields.body">No translation key set!</span>
    <TipTap v-else v-model="value" mode="single" />
  </component>
</template>

<style scoped>
.text-single {
  font-family: v-bind(textFontFamily);
  font-weight: v-bind(textFontWeight);
  color: v-bind(textColor);
  background-image: v-bind(textGradient);
  font-size: v-bind(textFontSize);
  line-height: v-bind(textLineHeight);
  text-align: v-bind(textAlign);
  padding: v-bind(textPadding);
  margin: v-bind(textMargin);
  max-width: v-bind(textMaxWidth);
}

/* Heading-only UA/brand properties .text-single does not declare. Inherited
   properties get `inherit` (= what the div gets); text-decoration is not
   inherited, so it is reset to its initial value. No-op on the div. */
.text-single:is(h1, h2, h3, h4) {
  font-style: inherit;
  letter-spacing: inherit;
  word-spacing: inherit;
  text-transform: inherit;
  text-shadow: inherit;
  text-wrap: inherit;
  text-decoration: none;
}

/* Paint the gradient into the glyphs themselves via background-clip: text.
   Scoped to .has-gradient so a plain solid color never picks up a stray
   -webkit-text-fill-color: transparent from an unset background-image. */
.text-single.has-gradient {
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Single-line truncation: cap the width (via max-width above) and clip the
   rendered text with an ellipsis. Two DOM shapes must be covered:
   - Live/built pipe: the builder's TipTap is a static shim (TipTapRenderer)
     that emits inline <span>s directly inside .text-single — so the ellipsis
     lives on .text-single itself.
   - Editor: the full ProseMirror editor renders EditorContent → .ProseMirror
     (→ <p> in multiline) — clipped via the deep rules. */
.text-single.is-truncated {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-single.is-truncated :deep(.tiptap-editor-content),
.text-single.is-truncated :deep(.ProseMirror),
.text-single.is-truncated :deep(p) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
</style>
